import React, { useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  Download,
  FileText,
  Loader2,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import { UrlContext } from "../../contexts/UrlContext";

function formatTranscriptDate(value) {
  if (!value) return "Unknown date";

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "Unknown date";
  }

  return parsed.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function parseTranscriptTime(time = "") {
  const parts = String(time).split(":");

  if (parts.length < 3) return time;

  const [hh, mm, ssRaw] = parts;
  const ss = String(ssRaw || "").split(".")[0];

  return `${hh}:${mm}:${ss}`;
}

function cleanTranscriptText(text = "") {
  return String(text)
    .replace(/<\/?v[^>]*>/g, "")
    .replace(/<[^>]*>/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function parseWebVttTranscript(content = "") {
  const blocks = String(content || "")
    .replace("WEBVTT", "")
    .split(/\r?\n\r?\n/)
    .map((block) => block.trim())
    .filter(Boolean);

  return blocks
    .map((block, index) => {
      const lines = block.split(/\r?\n/);
      const timeLine = lines.find((line) => line.includes("-->"));

      if (!timeLine) return null;

      const textLines = lines.filter(
        (line) => line && !line.includes("-->") && !/^\d+$/.test(line.trim())
      );

      const rawText = textLines.join(" ");
      const speakerMatch = rawText.match(/<v\s+([^>]+)>/);
      const speaker = speakerMatch?.[1] || "Unknown";

      const [startRaw, endRaw] = timeLine
        .split("-->")
        .map((value) => value.trim());

      return {
        id: `${startRaw}-${index}`,
        speaker,
        start: parseTranscriptTime(startRaw),
        end: parseTranscriptTime(endRaw),
        text: cleanTranscriptText(rawText),
      };
    })
    .filter((item) => item?.text);
}

function normalizeTranscriptList(transcripts = []) {
  return transcripts.flatMap((transcript, transcriptIndex) => {
    const parsed = parseWebVttTranscript(transcript.content);

    return parsed.map((item) => ({
      ...item,
      transcriptId: transcript.id,
      transcriptIndex,
      createdDateTime: transcript.createdDateTime,
    }));
  });
}

function getInitials(name = "") {
  const parts = name.trim().split(" ").filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || "?";

  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}

function getAvatarColor(name = "") {
  const colors = [
    "bg-blue-100 text-blue-700",
    "bg-emerald-100 text-emerald-700",
    "bg-purple-100 text-purple-700",
    "bg-amber-100 text-amber-700",
    "bg-rose-100 text-rose-700",
    "bg-cyan-100 text-cyan-700",
  ];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
}

function TeamsAvatar({ name }) {
  return (
    <div
      className={[
        "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
        getAvatarColor(name),
      ].join(" ")}
    >
      {getInitials(name)}
    </div>
  );
}

function TranscriptList({ transcripts = [], onOpenTranscript }) {
  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-semibold text-slate-900">
          Meeting transcripts
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Select one transcript to view its content.
        </p>
      </div>

      <div className="space-y-2">
        {transcripts.map((transcript, index) => (
          <button
            key={transcript.id || index}
            type="button"
            onClick={() => onOpenTranscript(transcript.id)}
            className="flex w-full items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left transition hover:border-[#365561]/30 hover:bg-[#365561]/5"
          >
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#365561]/10 text-[#365561]">
                <FileText className="h-5 w-5" />
              </div>

              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-900">
                  Transcript {index + 1}
                </p>
                <p className="mt-0.5 text-xs text-slate-500">
                  {formatTranscriptDate(transcript.createdDateTime)}
                </p>
              </div>
            </div>

            <span className="shrink-0 text-xs font-semibold text-[#365561]">
              Open
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function TeamsTranscriptSection({ transcript, searchTerm = "" }) {
  const items = useMemo(() => {
    return normalizeTranscriptList(transcript ? [transcript] : []);
  }, [transcript]);

  const filteredItems = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    if (!query) return items;

    return items.filter((item) => {
      return (
        item.text.toLowerCase().includes(query) ||
        item.speaker.toLowerCase().includes(query) ||
        item.start.toLowerCase().includes(query)
      );
    });
  }, [items, searchTerm]);

  if (!filteredItems.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center">
        <p className="text-sm font-medium text-slate-600">
          {searchTerm ? "No transcript lines matched your search." : "No transcript found."}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {filteredItems.map((item, index) => {
        const previous = filteredItems[index - 1];
        const sameSpeakerAsPrevious = previous?.speaker === item.speaker;

        return (
          <div
            key={`${item.transcriptId}-${item.id}`}
            className={[
              "flex items-start gap-3",
              sameSpeakerAsPrevious ? "pt-0" : "pt-3",
            ].join(" ")}
          >
            <div className="relative w-7 shrink-0">
              {!sameSpeakerAsPrevious && (
                <div className="absolute top-6">
                  <TeamsAvatar name={item.speaker} />
                </div>
              )}
            </div>

            <div className="min-w-0 flex-1">
              {!sameSpeakerAsPrevious && (
                <div className="mb-1 flex flex-wrap items-baseline gap-2">
                  <p className="text-sm font-semibold text-slate-800">
                    {item.speaker}
                  </p>
                  <p className="text-[11px] font-medium text-slate-400">
                    {item.start}
                  </p>
                </div>
              )}

              <div
                className={[
                  "max-w-[88%] rounded-2xl bg-slate-100 px-4 py-2 text-sm leading-6 text-slate-700",
                  sameSpeakerAsPrevious
                    ? "ml-0 rounded-tl-2xl"
                    : "rounded-tl-sm",
                ].join(" ")}
              >
                {item.text}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PrintTranscriptSection({ transcript }) {
  const items = useMemo(() => {
    return normalizeTranscriptList(transcript ? [transcript] : []);
  }, [transcript]);

  return (
    <div className="transcript-print-root hidden print:block">
      <div className="mb-6 border-b border-slate-200 pb-4">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#365561]">
          Meeting transcript
        </p>

        <h1 className="mt-2 text-2xl font-bold text-slate-900">
          Teams Call
        </h1>

        <p className="mt-2 text-sm text-slate-600">
          {formatTranscriptDate(transcript?.createdDateTime)}
        </p>
      </div>

      <div className="space-y-3">
        {items.map((item, index) => {
          const previous = items[index - 1];
          const sameSpeakerAsPrevious = previous?.speaker === item.speaker;

          return (
            <div
              key={`${item.transcriptId}-${item.id}-print`}
              className={[
                "transcript-print-message flex items-start gap-3 break-inside-avoid",
                sameSpeakerAsPrevious ? "pt-0" : "pt-3",
              ].join(" ")}
            >
              <div className="relative w-8 shrink-0">
                {!sameSpeakerAsPrevious && (
                  <div className="absolute top-6">
                    <TeamsAvatar name={item.speaker} />
                  </div>
                )}
              </div>

              <div className="min-w-0 flex-1">
                {!sameSpeakerAsPrevious && (
                  <div className="mb-1 flex flex-wrap items-baseline gap-2">
                    <p className="text-sm font-semibold text-slate-800">
                      {item.speaker}
                    </p>
                    <p className="text-[11px] font-medium text-slate-400">
                      {item.start}
                    </p>
                  </div>
                )}

                <div
                  className={[
                    "max-w-[88%] rounded-2xl bg-slate-100 px-4 py-2 text-sm leading-6 text-slate-700",
                    sameSpeakerAsPrevious
                      ? "ml-0 rounded-tl-2xl"
                      : "rounded-tl-sm",
                  ].join(" ")}
                >
                  {item.text}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function MeetingTranscript({
  transcripts=[],
  setIsViewTranscriptDetails,
  onClose,
  onBack,
}) {
  const [selectedTranscriptId, setSelectedTranscriptId] = useState("");
  const [isListView, setIsListView] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (transcripts) {
      setSelectedTranscriptId(transcripts[0]?.id || "");
      setIsListView(transcripts.length > 1);
    }
  }, [transcripts]);

  const selectedTranscript = useMemo(() => {
    return (
      transcripts.find((item) => item.id === selectedTranscriptId) ||
      transcripts[0] ||
      null
    );
  }, [transcripts, selectedTranscriptId]);

  const closeModal = () => {
    if (onClose) {
      onClose();
      return;
    }

    if (setIsViewTranscriptDetails) {
      setIsViewTranscriptDetails(false);
    }
  };

  const handleBack = () => {
    if (isListView || transcripts.length <= 1) {
      if (onBack) {
        onBack();
        return;
      }

      closeModal();
      return;
    }

    setIsListView(true);
    setSearchTerm("");
  };

  useEffect(() => {
    document.body.classList.add("overflow-hidden");

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, []);

  const handlePrintTranscript = () => {
    document.body.classList.add("printing-transcript-ready");

    setTimeout(() => {
      window.print();
      setTimeout(() => {
        document.body.classList.remove("printing-transcript-ready");
      }, 300);
    }, 50);
  };

  const openTranscript = (transcriptId) => {
    setSelectedTranscriptId(transcriptId);
    setSearchTerm("");
    setIsListView(false);
  };

  return (
    <>
      <style>{`
        .transcript-print-root {
          display: none;
        }

        @page {
          size: A4;
          margin: 14mm;
        }

        @media print {
          html,
          body {
            margin: 0 !important;
            padding: 0 !important;
            background: #ffffff !important;
            overflow: visible !important;
            height: auto !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          body.printing-transcript-ready > #root > * {
            display: none !important;
          }

          .transcript-print-root {
            display: block !important;
            position: static !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            color: #0f172a !important;
            overflow: visible !important;
            height: auto !important;
            box-shadow: none !important;
          }

          .transcript-print-root,
          .transcript-print-root * {
            visibility: visible !important;
          }

          .no-print {
            display: none !important;
          }

          .transcript-print-message {
            break-inside: avoid !important;
            page-break-inside: avoid !important;
            margin-bottom: 10px !important;
          }

          .transcript-print-root * {
            box-shadow: none !important;
            animation: none !important;
            transition: none !important;
            transform: none !important;
          }
        }
      `}</style>

      {createPortal(
        <PrintTranscriptSection transcript={selectedTranscript} />,
        document.body
      )}

      <motion.div
        className="fixed inset-0 z-40 bg-slate-900/30 backdrop-blur-sm no-print"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={closeModal}
      />

      <div className="fixed top-[78px] inset-0 z-50 flex items-center justify-center p-4 no-print">
        <motion.div
          key="transcript-modal"
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.98 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
          className="flex h-[calc(100vh-100px)] w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.22)]"
        >
          <div className="flex items-center justify-between gap-3 border-b border-slate-100 bg-white px-5 py-3 shadow-sm">
            <div>
              <p className="text-sm font-semibold text-slate-900">
                Meeting transcript
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Full conversation transcript with speaker attribution and timestamps.
              </p>
            </div>

            <div className="flex flex-row gap-2">
              <button
                type="button"
                onClick={handlePrintTranscript}
                disabled={!selectedTranscript}
                className="inline-flex items-center gap-2 rounded-xl bg-[#365561] px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#2c444d] disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none"
              >
                <Download className="h-3.5 w-3.5" />
                PDF
              </button>

              {transcripts.length > 1 && !isListView ? (
                <button
                  type="button"
                  onClick={handleBack}
                  className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 focus:outline-none"
                >
                  Back
                </button>
              ) : null}

              <button
                type="button"
                onClick={closeModal}
                className="rounded-2xl border border-slate-200 bg-white p-2 text-slate-500 transition hover:bg-slate-50 focus:outline-none"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            <AnimatePresence mode="wait">
              <motion.div
                key={isListView ? "transcript-list" : "transcript-view"}
                initial={{ opacity: 0, x: 36 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -36 }}
                transition={{ duration: 0.22, ease: "easeOut" }}
                className="h-full"
              >
                {isListView ? (
                  <TranscriptList
                    transcripts={transcripts}
                    onOpenTranscript={openTranscript}
                  />
                ) : (
                  <div className="space-y-4">
                    {transcripts.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => {
                          setIsListView(true);
                          setSearchTerm("");
                        }}
                        className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500 transition hover:text-[#365561]"
                      >
                        <ArrowLeft className="h-4 w-4" />
                        Back to transcript list
                      </button>
                    ) : null}

                    <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2">
                      <Search className="h-4 w-4 text-slate-400" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        placeholder="Search transcript..."
                        className="w-full bg-transparent text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none"
                      />
                    </div>

                    <TeamsTranscriptSection
                      transcript={selectedTranscript}
                      searchTerm={searchTerm}
                    />
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </>
  );
}
